'use client'

import * as React from 'react'
import { Button } from '@repo/design-system-cf/components/button'
import { IconDownload, IconDone } from '@repo/design-system-cf/components/icons'
import { Link } from '@repo/web-ui/components/link'
import { useDownloadLegacyProduct } from '@repo/web-ui/hooks/use-download-legacy-product'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useDownloadContentProduct } from '@web/hooks/use-download-content-product'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint,
} from 'payments/payments-dialog/analytics'
import { PaymentFailedModal } from 'payments/components/payment-failed-modal/payment-failed-modal'
import type { PaymentStatus } from 'codegen/types'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'

interface ProductData {
  category: string
  subcategory?: string
  imageUrl: string
  name: string
}

interface UseDownloadProps {
  productId: string
  isLegacy: boolean
  isFree: boolean
  productData: ProductData
  onSuccess?: () => void
}

function useDownload({ productId, isLegacy, isFree, productData, onSuccess }: UseDownloadProps) {
  const {
    download: downloadContentProduct,
    isLoading: isLoadingContentProduct,
    payment,
    resetPayment,
  } = useDownloadContentProduct(productId, {
    enabled: !isLegacy,
  })
  const {
    download: downloadLegacyProduct,
    isLoading: isLoadingLegacyProduct,
    paymentError,
    resetPaymentError,
  } = useDownloadLegacyProduct()

  const downloadLoading = isLoadingContentProduct || isLoadingLegacyProduct

  // oxlint-disable-next-line require-await
  const handleDownload = async () => {
    if (downloadLoading) {
      return
    }

    savePaymentModalEntryPoint(PAYMENT_ENTRY_POINTS.SEARCH_PRODUCT_CARD_DOWNLOAD)

    if (isLegacy) {
      downloadLegacyProduct({
        productId,
        isFree,
        onSuccess: () => {
          onSuccess?.()
        },
      })

      sendAmplitudeEvent(AmplitudeEvents.DOWNLOADED_PRODUCT, {
        category: productData.category,
        subcategory: productData.subcategory ? [productData.subcategory] : [],
        image_url: productData.imageUrl,
        name: productData.name,
        product_id: productId,
        productId,
        type: 'subscription',
        isLegacy,
        download_origin: 'product_card',
      })
    } else {
      // downloadContentProduct will trigger the amplitude event internally
      downloadContentProduct({})
    }
  }

  return {
    isLoading: downloadLoading,
    download: handleDownload,
    payment,
    paymentError,
    resetPayment,
    resetPaymentError,
  }
}

export function ProductDownload({
  productId,
  isLegacy,
  isFree = false,
  productData,
  className,
}: {
  productId: string
  isLegacy: boolean
  isFree?: boolean
  productData: ProductData
  className?: string
}) {
  const t = useTranslations('components.product_card')
  const { data: isLoggedIn } = useIsLoggedInQuery()

  const handleDownload = React.useCallback(() => {
    setIsDownloaded(true)
  }, [])

  const { download, isLoading, payment, resetPayment, paymentError, resetPaymentError } =
    useDownload({
      productId,
      isLegacy,
      isFree,
      productData,
      onSuccess: handleDownload,
    })
  const [isDownloaded, setIsDownloaded] = React.useState(false)

  const handleClosePaymentFailedModal = () => {
    if (payment?.paymentUrl) {
      resetPayment()
    } else {
      resetPaymentError()
    }
  }

  let paymentErrorCombined: PaymentStatus | null = null

  if (payment?.paymentUrl) {
    paymentErrorCombined = payment.status
  } else if (paymentError?.message) {
    paymentErrorCombined = paymentError.status
  }

  if (!isLoggedIn) {
    return (
      <Link
        onClick={() =>
          savePaymentModalEntryPoint(PAYMENT_ENTRY_POINTS.SEARCH_PRODUCT_CARD_DOWNLOAD)
        }
        href={AppRoutes.B_FREE_TRIAL}
        className={tw('flex', className)}
      >
        <Button
          disabled={isLoading}
          variant="tertiary-grey"
          iconLeft={<IconDownload />}
          size="small"
          textClassName="hidden @[10rem]:inline"
        >
          {t('download')}
        </Button>
      </Link>
    )
  }

  if (payment?.paymentUrl) {
    return null
  }

  return (
    <>
      <Button
        className={className}
        onClick={download}
        disabled={isLoading}
        iconLeft={isDownloaded ? <IconDone /> : <IconDownload />}
        variant={isDownloaded ? 'tertiary' : 'primary-green-bright'}
        size="small"
        textClassName="hidden @[10rem]:inline"
      >
        {isDownloaded ? t('downloaded') : t('download')}
      </Button>
      <PaymentFailedModal
        paymentStatus={paymentErrorCombined}
        message={paymentError?.url || payment?.paymentUrl || null}
        closeModal={handleClosePaymentFailedModal}
      />
    </>
  )
}
